import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Backdrop,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";

const ReferralPage = () => {
  const { user, token } = useAuthContext();
  const { darkMode } = useContext(ThemeContext);
  const [filteredReferrals, setFilteredReferrals] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "asc",
  });

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      return "N/A";
    }
  };

  useEffect(() => {
    // Fetch referrals from the backend
    if (!user || !token) return;
    const fetchReferrals = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/referrals/get-referrals`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = response.data;

        // Map the backend response to match your data structure
        const mappedReferrals = data?.referrals?.map((referral) => ({
          id: referral.id,
          sendUserName: referral.sendUserName || referral.sender_username || "N/A",
          sendUserEmail: referral.sendUserEmail || referral.sender_email || "N/A",
          referredUserEmail: referral.referredUserEmail || referral.referred_email || "N/A",
          createdAt: referral.createdAt || referral.created_at || "N/A",
          status: referral.status || "N/A",
        })) || [];

        setReferrals(mappedReferrals);
        setFilteredReferrals(mappedReferrals);
      } catch (err) {
        console.error("Error fetching referrals:", err);
        enqueueSnackbar("Error fetching referrals", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchReferrals();
  }, [token, user, enqueueSnackbar]);

  useEffect(() => {
    // Filter referrals based on the search query
    setFilteredReferrals(
      referrals.filter((referral) => {
        return (
          (referral.sendUserName || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (referral.sendUserEmail || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (referral.referredUserEmail || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (referral.id?.toString() || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (referral.status || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        );
      })
    );
  }, [searchQuery, referrals]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSort = (column) => {
    const isAscending =
      sortConfig.key === column && sortConfig.direction === "asc";
    const newDirection = isAscending ? "desc" : "asc";

    setSortConfig({
      key: column,
      direction: newDirection,
    });

    // Sort referrals based on the column and direction
    const sortedReferrals = [...referrals].sort((a, b) => {
      if (a[column] < b[column]) return newDirection === "asc" ? -1 : 1;
      if (a[column] > b[column]) return newDirection === "asc" ? 1 : -1;
      return 0;
    });

    setReferrals(sortedReferrals);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedReferrals = filteredReferrals.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box
      sx={{
        pt: "60px",
        pl: 5,
        pr: 10,
      }}
    >
      {/* Loading State */}
      <Backdrop sx={{ zIndex: 1400 }} open={loading}>
        <CircularProgress size={80} />
      </Backdrop>

      {!loading && (
        <>
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontSize: "28px",
              color: darkMode
                ? getColors.drawerTextDark
                : getColors.thumbnailTextLight,
              fontWeight: 600,
              textAlign: "left",
              mb: 5,
            }}
          >
            Referral Management
          </Typography>
          
          <Box display="flex" justifyContent="flex-start" sx={{ mb: 3 }}>
            <TextField
              variant="filled"
              placeholder="Search referrals"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    <SearchIcon sx={{ mt: -2, ml: 1 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer component={Paper} sx={{ mb: 1 }}>
            <Table>
              <TableHead
                sx={{
                  background: darkMode ? "#1C1D21" : "#E6E6E6",
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: darkMode
                    ? getColors.appBarBorderDark
                    : getColors.thumbnailBorderLight,
                }}
              >
                <TableRow sx={{ height: "60px" }}>
                  {[
                    "id",
                    "sendUserName",
                    "sendUserEmail",
                    "referredUserEmail",
                    "createdAt",
                    "status",
                  ].map((header, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                        px: 0,
                        cursor: "pointer",
                      }}
                      onClick={() => handleSort(header)}
                    >
                      <Box
                        display="flex"
                        sx={{
                          width: "100%",
                          height: "100%",
                          alignItems: "center",
                          fontFamily: "Poppins",
                          fontWeight: 500,
                          fontSize: "18px",
                          color: darkMode
                            ? getColors.listItemTextDark
                            : getColors.thumbnailTextLight,
                          px: "20px",
                          ...(header === "id" && { justifyContent: "center" }),
                        }}
                      >
                        {header === "id"
                          ? "ID"
                          : header === "sendUserName"
                          ? "SENDER NAME"
                          : header === "sendUserEmail"
                          ? "SENDER EMAIL"
                          : header === "referredUserEmail"
                          ? "REFERRED EMAIL"
                          : header === "createdAt"
                          ? "DATE SENT"
                          : "STATUS"}
                        {sortConfig.key === header && (
                          <>
                            {sortConfig.direction === "asc" ? (
                              <ArrowDownwardIcon sx={{ ml: 1 }} />
                            ) : (
                              <ArrowUpwardIcon sx={{ ml: 1 }} />
                            )}
                          </>
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedReferrals.map((referral) => (
                  <TableRow
                    key={referral.id}
                    sx={{
                      height: "60px",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.appBarBorderDark
                        : getColors.thumbnailBorderLight,
                    }}
                  >
                    <TableCell
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontSize: "18px",
                        color: darkMode
                          ? getColors.listItemTextDark
                          : getColors.thumbnailTextLight,
                        textAlign: "center",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      {referral.id}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontSize: "18px",
                        color: darkMode
                          ? getColors.listItemTextDark
                          : getColors.thumbnailTextLight,
                        px: "20px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      {referral.sendUserName}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "18px",
                        color: darkMode
                          ? getColors.listItemTextDark
                          : getColors.thumbnailTextLight,
                        px: "20px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      {referral.sendUserEmail}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "18px",
                        color: darkMode
                          ? getColors.listItemTextDark
                          : getColors.thumbnailTextLight,
                        px: "20px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      {referral.referredUserEmail}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "18px",
                        color: darkMode
                          ? getColors.listItemTextDark
                          : getColors.thumbnailTextLight,
                        px: "20px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      {formatDate(referral.createdAt)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontSize: "18px",
                        color: darkMode
                          ? getColors.listItemTextDark
                          : getColors.thumbnailTextLight,
                        px: "20px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                        textTransform: "capitalize",
                      }}
                    >
                      {referral.status}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={filteredReferrals.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Box>
  );
};

export default ReferralPage;